/**
 * ImageUploader — file picker que sube a `/api/v1/images/upload` (multipart)
 * y entrega la URL pública del asset al callback.
 *
 * Acepta múltiples imágenes secuencialmente; muestra previews ordenadas.
 * Reusa los mismos headers de tenant que el resto del admin.
 */
import { useRef, useState } from 'react'
import { Image as ImageIcon, Upload, X, Loader2 } from 'lucide-react'
import { useAuth, tenantHeaders } from '@/lib/auth-store'
import { Button } from '@/components/ui'
import { cn } from '@/lib/cn'

interface UploadResponse {
  data: { key: string; url: string; size: number }
}

export interface ImageUploaderProps {
  value: string[]
  onChange: (urls: string[]) => void
  folder?: string
  maxImages?: number
  className?: string
}

export function ImageUploader({
  value,
  onChange,
  folder = 'products',
  maxImages = 10,
  className,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { apiUrl } = useAuth()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function uploadOne(file: File): Promise<string> {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('folder', folder)
    const res = await fetch(`${apiUrl}/api/v1/images/upload`, {
      method: 'POST',
      headers: tenantHeaders(),
      body: fd,
    })
    const json = await res.json().catch(() => null) as UploadResponse | { error?: { message?: string } } | null
    if (!res.ok) {
      const msg = (json as { error?: { message?: string } } | null)?.error?.message ?? `HTTP ${res.status}`
      throw new Error(msg)
    }
    return (json as UploadResponse).data.url
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setError(null)
    setUploading(true)
    try {
      const remaining = maxImages - value.length
      const arr = Array.from(files).slice(0, remaining)
      const urls: string[] = []
      for (const f of arr) {
        const url = await uploadOne(f)
        urls.push(url)
      }
      onChange([...value, ...urls])
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx))
  }

  const reachedMax = value.length >= maxImages

  return (
    <div className={cn('space-y-3', className)}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {value.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className="relative aspect-square rounded-md border border-border bg-background overflow-hidden group"
            >
              <img src={url} alt={`imagen ${idx + 1}`} className="size-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 size-6 rounded-full bg-black/70 text-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title="Eliminar"
              >
                <X className="size-3.5" />
              </button>
              {idx === 0 && (
                <span className="absolute bottom-1 left-1 text-[9px] font-mono uppercase bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {!reachedMax && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <><Loader2 className="size-4 mr-2 animate-spin" /> Subiendo...</>
          ) : (
            <><Upload className="size-4 mr-2" /> {value.length === 0 ? 'Subir imágenes' : `Agregar más (${value.length}/${maxImages})`}</>
          )}
        </Button>
      )}

      {value.length === 0 && !uploading && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <ImageIcon className="size-3.5" /> JPG, PNG, WebP, GIF, AVIF o SVG · máx 10MB cada una
        </p>
      )}

      {error && (
        <p className="text-xs text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1.5">{error}</p>
      )}
    </div>
  )
}
